import {
  AdornedInput,
  Adornment,
  Alert,
  Anchor,
  Button,
  ButtonGroup,
  Card,
  Form,
  FormField,
  FormHelp,
  Grid,
  Heading,
  Input,
  Label,
  LoadingButton,
  ModalDialog,
  Paragraph,
  Text,
  Textarea,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation';
import { validate } from 'email-validator';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useAddressAutocomplete } from '../../hooks/useAddressAutocomplete';
import { AddressAutocomplete, type AddressResponse } from '../AddressAutocomplete';
import { useEffect, useState } from 'react';
import { logError } from '@zg-rentals/log-error';
import { useGetReportsByZip } from '../../hooks/useGetReports';
import {
  GetApplicationsForUserDocument,
  ShareApplicationDocument,
  type ShareRentalApplicationPropertyAddressInput,
  type UserStatus,
  UserStatusType,
} from '../../graphql/queries';
import { BlockLoadingMask } from '../../styles/styled-components';
import { SUBMISSION_TYPES, useBaseContext } from '../../contexts/BaseContext';
import { useMutation } from '@apollo/client';
import { CreditReportOverview } from '../Reports/ReportCards/CreditReportOverview';
import { BackgroundReportOverview } from '../Reports/ReportCards/BackgroundReportOverview';
import type { ReportType } from '../../models';
import { ReportModal } from '../Reports';
import { useIsFetching } from '@zg-rentals/react-query5';
import useGetUser from '../../hooks/useGetUser';
import { APPLICATIONS_FAQ_URL } from '../../constants/urls';
import { emitShareApplicationNextEvent, emitShareApplicationSharedEvent } from '../../analytics/clickstreamEvents';
import { bootstrap } from '../../bootstrap';
import { transformReportData } from '../../utils/reports/report-utils';
import { useApplicationContextOrUndefined } from '../../contexts/ApplicationContext';

type ShareFormData = {
  landlordEmail: string;
  addressString: string;
  propertyAddress?: ShareRentalApplicationPropertyAddressInput;
  unit?: string;
  message?: string;
};

const emailMaxLengthValidation = { value: 320, message: 'Enter an email under 320 characters' };
const unitMaxLengthValidation = { value: 16, message: 'Enter a unit under 16 characters' };
const messageMaxLengthValidation = { value: 10000, message: 'Enter a message under 10,000 characters' };

export function ApplicationShare({
  onClose,
  userStatuses,
  initialAddress,
  initialUnit,
}: {
  onClose: () => void;
  userStatuses: Array<UserStatus>;
  initialAddress?: string;
  initialUnit?: string;
}) {
  const { setSubmittedApplication } = useBaseContext();
  const applicationContext = useApplicationContextOrUndefined();
  const form = useForm<ShareFormData>();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = form;
  const [partialAddress, setPartialAddress, queryData] = useAddressAutocomplete({ initialAddress });
  const [isOnReview, setIsOnReview] = useState(false);
  const { enqueueToast } = useToast();
  const [shareMutation, { loading: isSharing }] = useMutation(ShareApplicationDocument, {
    refetchQueries: [GetApplicationsForUserDocument],
  });
  const { data: userData } = useGetUser();

  const addressString = watch('addressString');
  const zip = watch('propertyAddress.zip') as string;

  useEffect(() => {
    // If the url has ?shareAddress we need to wait for the data from the autocomplete lookup to finish
    // and then set the propertyAddress as if the user clicked the result
    const addresses = queryData?.data?.response?.addresses;
    if (initialAddress && addresses?.length && !addressString) {
      const address = addresses.find(({ name }) => name === initialAddress);
      if (address) {
        setValue('addressString', initialAddress);
        setValue('unit', initialUnit);
        setValue('propertyAddress', {
          propertyId: address.propertyId,
          street: address.street,
          city: address.city,
          state: address.state,
          zip: address.zip,
          unit: initialUnit,
        });
      }
    }
  }, [initialAddress, queryData?.data, addressString]);

  function onClickNext(_: ShareFormData) {
    setIsOnReview(true);
    emitShareApplicationNextEvent(applicationContext);
  }

  async function onClickShare(data: ShareFormData) {
    if (data.propertyAddress) {
      try {
        emitShareApplicationSharedEvent(applicationContext);
        await shareMutation({
          variables: {
            landlordEmail: data.landlordEmail,
            propertyAddress: {
              ...data.propertyAddress,
              unit: data.unit,
            },
            message: data.message,
          },
        });
        bootstrap.monitor.count({ name: `ApplicationShare onClickShare` });
        bootstrap.monitor.count({ name: `ApplicationShare onClickShare state:${data.propertyAddress.state}` });
        setSubmittedApplication?.({
          type: SUBMISSION_TYPES.SHARED,
          address: {
            street: data.propertyAddress.street,
            city: data.propertyAddress.city,
            state: data.propertyAddress.state,
            zip: data.propertyAddress.zip,
            unit: data.unit,
          },
        });
        onClose();
      } catch (err) {
        logError({ error: err, context: 'ApplicationShare - shareApplicationMutation error' });
        enqueueToast(<Toast appearance="error" body="We couldn’t share your application. Try again." />);
      }
    }
  }

  const formSubmitHandler = form.handleSubmit(onClickNext);
  const isFetching = !!useIsFetching();

  return (
    <FormProvider {...form}>
      <Form onSubmit={formSubmitHandler}>
        <ModalDialog
          isOpen
          onClose={onClose}
          size={{ default: 'fullScreen', md: 'lg' }}
          renderHeader={<Heading level={1}>Share application</Heading>}
          renderBody={
            !isOnReview ? (
              <>
                <Paragraph fontType="bodySmall">
                  You can add a personal note to share with the rental manager along with your application. We suggest
                  checking with the rental manager to make sure they accept Zillow applications.
                </Paragraph>
                <FormField
                  required
                  marginY="sm"
                  label={<Label>Rental manager’s email</Label>}
                  control={
                    <Input
                      {...register('landlordEmail', {
                        required: 'Email is required',
                        maxLength: emailMaxLengthValidation,
                        validate: (email: string) => {
                          if (!validate(email)) {
                            return 'Enter a valid email';
                          } else if (email === userData?.email) {
                            return 'Application can’t be shared with your own email';
                          } else {
                            return true;
                          }
                        },
                      })}
                    />
                  }
                  description={errors.landlordEmail && <FormHelp>{errors.landlordEmail.message}</FormHelp>}
                  error={!!errors.landlordEmail}
                />
                <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="md">
                  <Grid gridColumnEnd={{ default: 'span 12', md: 'span 6' }}>
                    <Controller
                      name="addressString"
                      rules={{
                        required: 'A USPS validated address is required',
                      }}
                      render={({ field: { onChange, value } }) => {
                        const handleAddressSelect = (newAddress: AddressResponse) => {
                          onChange(newAddress.name);
                          setValue('propertyAddress', {
                            propertyId: newAddress.propertyId,
                            street: newAddress.street,
                            city: newAddress.city,
                            state: newAddress.state,
                            zip: newAddress.zip,
                          });
                        };

                        return (
                          <FormField
                            required
                            label={<Label id="property-address-label">Rental property address</Label>}
                            control={
                              <AddressAutocomplete
                                value={value}
                                onInputChange={() => onChange('')}
                                partialAddress={partialAddress}
                                onSetPartialAddress={setPartialAddress}
                                queryData={queryData}
                                onAddressSelect={handleAddressSelect}
                                error={!!errors.addressString}
                                aria-labelledby="property-address-label"
                              />
                            }
                            description={errors.addressString && <FormHelp>{errors.addressString.message}</FormHelp>}
                            error={!!errors.addressString}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid gridColumnEnd={{ default: 'span 12', md: 'span 6' }}>
                    <FormField
                      marginBottom="sm"
                      label={<Label>Unit number</Label>}
                      control={
                        <AdornedInput
                          leftAdornment={<Adornment>#</Adornment>}
                          input={
                            <Input
                              {...register('unit', {
                                maxLength: unitMaxLengthValidation,
                              })}
                            />
                          }
                        />
                      }
                      description={errors.unit && <FormHelp>{errors.unit.message}</FormHelp>}
                      error={!!errors.unit}
                      optional
                    />
                  </Grid>
                </Grid>
                <FormField
                  label={<Label>Add a message</Label>}
                  control={
                    <Textarea
                      resize="vertical"
                      {...register('message', {
                        maxLength: messageMaxLengthValidation,
                      })}
                    />
                  }
                  description={errors.message && <FormHelp>{errors.message.message}</FormHelp>}
                  error={!!errors.message}
                  optional
                />
              </>
            ) : (
              <ShareReview zip={zip} userStatuses={userStatuses} />
            )
          }
          renderFooter={
            !isOnReview ? (
              <ButtonGroup aria-label="Share application actions">
                <TextButton onClick={onClose}>Cancel</TextButton>
                <Button buttonType="primary" onClick={handleSubmit(onClickNext)}>
                  Next
                </Button>
              </ButtonGroup>
            ) : (
              <ButtonGroup aria-label="Share application actions">
                <TextButton onClick={() => setIsOnReview(false)}>Back</TextButton>
                <LoadingButton
                  buttonType="primary"
                  onClick={handleSubmit(onClickShare)}
                  loading={isSharing}
                  disabled={isFetching}
                >
                  Share application
                </LoadingButton>
              </ButtonGroup>
            )
          }
        />
      </Form>
    </FormProvider>
  );
}

function ShareReview({ zip, userStatuses }: { zip: string; userStatuses: Array<UserStatus> }) {
  const { data, isPending, isError } = useGetReportsByZip({ zip, userStatuses });
  const reportData = transformReportData(data);
  const [reportType, setReportType] = useState<ReportType>();

  function onViewCreditReport() {
    setReportType(UserStatusType.creditReport);
  }

  function onViewBackgroundReport() {
    setReportType(UserStatusType.backgroundReport);
  }

  return (
    <>
      <Heading level={4} as="h2">
        One last thing to review
      </Heading>
      <Paragraph fontType="bodySmall" marginBottom="sm">
        Before you share, review your reports. We’re showing you this because some things, like your credit score, may
        have changed.
      </Paragraph>
      <BlockLoadingMask loading={isPending}>
        {!isError ? (
          <>
            <Card cardElevation={0} cardType="secondary">
              <CreditReportOverview
                creditReport={reportData?.creditReport}
                userStatuses={userStatuses}
                onView={onViewCreditReport}
              />
            </Card>
            <Card cardElevation={0} cardType="secondary" marginTop="sm">
              <BackgroundReportOverview
                backgroundReport={reportData?.backgroundReport}
                userStatuses={userStatuses}
                onView={onViewBackgroundReport}
              />
            </Card>
          </>
        ) : (
          <Alert
            appearance="warning"
            body={
              <Text>
                Your screening reports couldn’t be pulled. Contact{' '}
                <Anchor href={APPLICATIONS_FAQ_URL} target="_blank" rel="noopener noreferrer">
                  customer support
                </Anchor>{' '}
                to report the issue. You can share your application without the reports.
              </Text>
            }
          />
        )}
      </BlockLoadingMask>
      {reportType && (
        <ReportModal
          userStatuses={userStatuses}
          reportType={reportType}
          reportData={reportData}
          onClose={() => setReportType(undefined)}
        />
      )}
    </>
  );
}
